import { ErrorHandler, Injectable, Injector } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { FrontErrorReportService } from '../_services/front-error-report.service';

/**
 * Catch global pour les erreurs JS non capturées par Angular.
 * Délègue au FrontErrorReportService (POST /api/errors), conserve console.error.
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  constructor(private injector: Injector) {}

  handleError(error: any): void {
    // Toujours logger en console pour le debug local
    console.error('[GlobalErrorHandler]', error);

    // Les HttpErrorResponse sont déjà gérées par ErrorInterceptor — éviter le doublon
    if (error instanceof HttpErrorResponse) return;

    try {
      const { type, message, stack } = this.extract(error);
      // Skip si on n'a pas d'info exploitable : pas de message ET pas de stack.
      // Évite de spammer le mail avec des "Unknown error" sans contexte.
      if (!message && !stack) return;

      const reporter = this.injector.get(FrontErrorReportService);
      reporter.report({ type, message, stack });
    } catch {
      // injection failed (early bootstrap) : on ne casse pas
    }
  }

  private extract(error: any): { type: string; message: string; stack: string } {
    // Cas standard : objet Error
    if (error instanceof Error) {
      return {
        type: error.name || 'Error',
        message: error.message || '',
        stack: error.stack || '',
      };
    }
    // String : message direct
    if (typeof error === 'string') {
      return { type: 'StringError', message: error, stack: '' };
    }
    // PromiseRejection wrappée par Angular zone.js : { rejection: ... }
    if (error && typeof error === 'object' && 'rejection' in error) {
      return this.extract((error as any).rejection);
    }
    // HttpErrorResponse aurait dû être filtré par le caller, mais au cas où
    if (error && typeof error === 'object' && 'status' in error && 'url' in error) {
      const e: any = error;
      return {
        type: 'HttpError',
        message: e.message || e.statusText || `HTTP ${e.status}`,
        stack: 'URL: ' + (e.url || '') + '\nStatus: ' + e.status,
      };
    }
    // Array ou objet brut : on JSON.stringify pour récupérer du contexte
    if (error && (Array.isArray(error) || typeof error === 'object')) {
      let dump = '';
      try {
        dump = JSON.stringify(error, this.safeReplacer(), 2).slice(0, 2000);
      } catch {
        dump = String(error);
      }
      // Si le dump est vide ou trivial (`{}`, `[]`), on retourne rien (skip côté caller)
      if (!dump || dump === '{}' || dump === '[]') {
        return { type: '', message: '', stack: '' };
      }
      return {
        type: Array.isArray(error) ? 'ArrayError' : (error.constructor?.name || 'ObjectError'),
        message: error.message || '',
        stack: dump,
      };
    }
    // Primitif (number, boolean, null, undefined) : pas exploitable
    return { type: '', message: '', stack: '' };
  }

  private safeReplacer() {
    const seen = new WeakSet();
    return (_key: string, value: any) => {
      if (value && typeof value === 'object') {
        if (seen.has(value)) return '[Circular]';
        seen.add(value);
      }
      return value;
    };
  }
}
